import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  BreakEvent,
  CustomAttribute,
  Job,
  MultiDayInput,
  User,
  DayOfWeek,
} from '@core/generated/gql-types';
import {
  computeShiftDurationHoursFromUnixMs,
  formatDobCalendarDisplay,
  formatDobCalendarIso,
  formatDurationHours,
  millisecondsToMinutes,
  utcMidnightFromIsoDate,
  type DobAddress,
} from '@core/util/datetime.util';
import {
  formatInJobTimezone,
  getJobTimezone,
} from '@core/util/jobTimezone.util';
import {
  formatJobDaysSchedule,
  formatTimeRangeAndDuration,
} from '@core/util/jobScheduleDisplay.util';
import { parseJobDayClockTime } from '@core/util/jobScheduleBounds.util';
import { getJobDaysForDateRange } from '@core/job/workingDays/workingDays.util';

dayjs.extend(utc);
dayjs.extend(timezone);

export { DEFAULT_SCHEDULE_TIME_LABEL } from '@core/util/jobScheduleDisplay.util';
import { pluralize } from '@core/util/utils';
import { formatJobTime12h } from '@/modules/jobs/utils/jobTime.util';
import { parseScheduleDateLocal } from '@core/util/jobScheduleBounds.util';

export { formatJobDaysSchedule, formatTimeRangeAndDuration };

const formatJobDayTimeAsHHmm = (
  time: string | null | undefined,
): string | null => {
  const parts = parseJobDayClockTime(time);
  if (!parts) return null;
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
};

/**
 * When updating a job, we need to build the days input for the API.
 * This is to ensure that the days input is always up to date with
 * the start and end dates.
 *
 * mobile clients render shift times from `Job.days.startTime` / `endTime`.
 *
 * @param startDateTime - The start date and time of the job.
 * @param endDateTime - The end date and time of the job.
 * @param startTime - The start time of the job.
 * @param endTime - The end time of the job.
 * @returns The days input for the API.
 */
export const buildDaysInputForJobUpdate = (
  startDateTime: dayjs.Dayjs,
  endDateTime: dayjs.Dayjs,
  startTime: string,
  endTime: string,
  workingDays?: DayOfWeek[],
): MultiDayInput => {
  return {
    days:
      workingDays && workingDays.length > 0
        ? workingDays
        : getJobDaysForDateRange(startDateTime, endDateTime),
    startTime,
    endTime,
  };
};

/** Calendar day of a stored dob, as `YYYY-MM-DD` for a date input. */
export const formatDobForInput = (
  dob: string | null | undefined,
  address?: DobAddress | null,
): string => {
  return formatDobCalendarIso(dob, address);
};

/** Long calendar day, for example `13 October 2001`. */
export const formatDobForDisplay = (
  dob: string | null | undefined,
  address?: DobAddress | null,
): string => {
  return formatDobCalendarDisplay(dob, address) || '-';
};

/** `YYYY-MM-DD` saved as midnight UTC. 13 October 2001 is `1002931200000`. */
export const formatDobForApi = (dateString: string): string => {
  return utcMidnightFromIsoDate(dateString);
};

export const formatOneWeekDateDisplay = (startDate: Dayjs | null): string => {
  if (!startDate) {
    return 'Select date';
  }

  const start = startDate;
  // End date is 6 days after start date to make a full week.
  const end = start.add(6, 'day');

  return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
};

export const formatDateRangeDisplay = (
  startDate: Dayjs | null,
  endDate: Dayjs | null,
): string => {
  if (!startDate || !endDate) {
    return 'Select date';
  }

  const start = startDate;
  const end = endDate;

  // If same date, show single date
  if (start.isSame(end, 'day')) {
    return start.format('D MMM YYYY');
  }

  // If same month and year, show: "1 – 7 MMM YYYY"
  if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
    return `${start.format('D')} – ${end.format('D MMM YYYY')}`;
  }

  // If same year, show: "1 MMM – 7 MMM YYYY"
  if (start.isSame(end, 'year')) {
    return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }

  // Different years, show: "1 MMM YYYY – 7 MMM YYYY"
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
};

/** Display a unix-ms instant in local time (12-hour clock). Not for `job.days` schedule strings. */
export const formatTime = (
  timeString?: string | null,
  _jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
) => {
  if (!timeString) return 'Not set';
  const ms = Number(timeString);
  if (!Number.isFinite(ms)) return 'Not set';
  const formatted = dayjs(ms).format('hh:mm A');
  return formatted === 'Invalid Date' ? 'Not set' : formatted;
};

/**
 * Calculate the logged time for a job. If a break time is provided, it will be
 * subtracted from the total duration.
 *
 * @param startTime - The start time of the job as a unix-ms timestamp string.
 * @param endTime - The end time of the job as a unix-ms timestamp string.
 * @param breakTimes - The break time of the job as a unix duration in milliseconds.
 * @returns The logged time as a string.
 */
export const formatLoggedTime = (
  startTime?: string | null,
  endTime?: string | null,
  breakTimes?: number | null,
) => {
  if (!startTime || !endTime) return 'Not set';
  const breakHours = millisecondsToMinutes(breakTimes ?? 0) / 60;
  const grossHours = computeShiftDurationHoursFromUnixMs(startTime, endTime);
  const netHours = Math.max(0, grossHours - breakHours);
  return `${formatDurationHours(netHours)} hrs`;
};

const formatHoursAndMinutes = (totalMinutes: number): string => {
  const roundedMinutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${pluralize('hr', hours)}`);
  }
  if (minutes > 0 || hours === 0) {
    parts.push(`${minutes} ${pluralize('min', minutes)}`);
  }
  return parts.join(' ');
};

/** Shift length minus breaks, as hours and minutes (for example `7 hrs 40 mins`). */
export const formatNetLoggedTime = (
  startTime?: string | null,
  endTime?: string | null,
  daySequenceBreaks?: BreakEvent[] | null,
): string => {
  const startMs = Number(startTime);
  const endMs = Number(endTime);
  if (
    !startTime ||
    !endTime ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs)
  ) {
    return 'Not set';
  }
  const grossMinutes = (endMs - startMs) / 60000;
  const breakMinutes = getBreakTimeInMinutes(daySequenceBreaks ?? null);
  return formatHoursAndMinutes(Math.max(0, grossMinutes - breakMinutes));
};

export const getBreakTimeInMinutes = (
  daySequenceBreaks: BreakEvent[] | null,
) => {
  const breaks = daySequenceBreaks || [];
  if (breaks.length === 0) {
    return 0;
  }
  return breaks.reduce((sum, b) => {
    const duration = b.duration || '0';
    const match = duration.match(/(\d+)/);
    return sum + (match ? millisecondsToMinutes(Number(match[1])) : 0);
  }, 0);
};

// Calculate break time
export const formatBreakTime = (daySequenceBreaks: BreakEvent[] | null) => {
  const breaks = daySequenceBreaks || [];
  if (breaks.length === 0) {
    return '0 mins';
  }
  const totalMinutes = getBreakTimeInMinutes(breaks);
  return totalMinutes > 0
    ? `${Math.round(totalMinutes)} ${pluralize('min', totalMinutes)}`
    : '0 mins';
};

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';

  const parsedDate = dayjs(parseInt(dateString, 10));
  if (!parsedDate.isValid()) {
    return dateString;
  }

  return parsedDate.format('D MMMM YYYY');
};

export const formatDateShort = (value: string | undefined) => {
  if (!value) return '—';

  try {
    return dayjs(Number(value)).format('d MMM YYYY');
  } catch {
    return value;
  }
};

/**
 * Convert a Unix-ms timestamp string to a human readable date and time format.
 *
 * @param value - Unix-ms timestamp string
 * @returns
 */
export const formatUnixMsToDateTime = (
  value: string | undefined | null,
  jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
) => {
  if (!value) return 'Not set';
  return (
    formatInJobTimezone(value, jobOrTimezone, 'h:mm A, D MMM YYYY') || 'Not set'
  );
};

/**
 * Build a unix-ms timestamp from a calendar date plus `HH:mm` wall-clock time.
 *
 * Uses local calendar parsing ({@link parseScheduleDateLocal}) — appropriate when
 * the user picks date/time in a form. Do not pass `job.days.startTime` here; pass
 * the picker value. For display of stored instants, use {@link formatInJobTimezone}.
 */
export const getTimestampFromTime = (
  dateISO: string,
  timeString: string,
): string => {
  const parts = parseJobDayClockTime(timeString);
  const hour = parts?.hour ?? 0;
  const minute = parts?.minute ?? 0;
  return parseScheduleDateLocal(dateISO)
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .valueOf()
    .toString();
};

/**
 * Scheduled start time as `HH:mm AM/PM` for time display (e.g. `JobDetails`).
 *
 * @param job - The job to get the scheduled start time for.
 * @returns The scheduled start time as `HH:mm AM/PM`
 */
export const getJobStartTimeString12h = (job: Job): string => {
  const startTimeRaw = job.days?.startTime || job.clockInTime;
  const startTimeFormatted = formatJobTime12h(startTimeRaw) ?? 'Not set';
  return startTimeFormatted;
};

/**
 * Scheduled end time as `HH:mm AM/PM` for time display (e.g. `JobDetails`).
 *
 * @param job - The job to get the scheduled end time for.
 * @returns The scheduled end time as `HH:mm AM/PM`
 */
export const getJobEndTimeString12h = (job: Job): string => {
  const endTimeRaw = job.days?.endTime || job.clockOutTime;
  const endTimeFormatted = formatJobTime12h(endTimeRaw) ?? 'Not set';
  return endTimeFormatted;
};

/**
 * Scheduled start time as `HH:mm` for time inputs (e.g. `TimeSelectors`).
 * Uses unix-ms `job.startDate` when set; otherwise `job.days?.startTime`.
 *
 * @param job - The job to get the scheduled start time for.
 * @returns The scheduled start time as `HH:mm`
 */
export const getJobStartTimeString = (job: Job): string => {
  if (job.days?.startTime) {
    const formatted = formatJobDayTimeAsHHmm(job.days.startTime);
    if (formatted) return formatted;
  }
  if (job.startDate) {
    return dayjs(Number(job.startDate)).tz(getJobTimezone(job)).format('HH:mm');
  }
  return '';
};

/**
 * Scheduled end time as `HH:mm` for time inputs (e.g. `TimeSelectors`).
 * Prefers `job.days?.endTime` so overnight jobs are not read from contract `endDate` wall clock.
 *
 * @param job - The job to get the scheduled end time for.
 * @returns The scheduled end time as `HH:mm`
 */
export const getJobEndTimeString = (job: Job): string => {
  if (job.days?.endTime) {
    const formatted = formatJobDayTimeAsHHmm(job.days.endTime);
    if (formatted) return formatted;
  }
  if (job.endDate) {
    return dayjs(Number(job.endDate)).tz(getJobTimezone(job)).format('HH:mm');
  }
  return '';
};

export const toDayjsFromDateAndTime = (
  date: string,
  time: string,
): Dayjs | null => {
  if (!date || !time) return null;

  return dayjs(date)
    .hour(Number(time.split(':')[0]))
    .minute(Number(time.split(':')[1]))
    .second(0)
    .millisecond(0);
};

export const isDateRangeOverlapping = (
  startDate1: string,
  endDate1: string,
  startDate2: string,
  endDate2: string,
) => {
  const day1Start = new Date(parseInt(startDate1 || '0', 10)).valueOf();
  const day1End = new Date(parseInt(endDate1, 10)).valueOf();
  const day2Start = new Date(parseInt(startDate2 || '0', 10)).valueOf();
  const day2End = new Date(parseInt(endDate2, 10)).valueOf();

  const day1BeforeDay2 = day1Start <= day2Start && day1End <= day2Start;
  const day1AfterDay2 = day1Start >= day2End && day1End >= day2End;
  const noOverlap = day1BeforeDay2 || day1AfterDay2;
  return !noOverlap;
};

/**
 * Generic utility to get a custom attribute value by ref from any object with customAttributes
 *
 * @param entity - An object that has a customAttributes field (e.g., Client, User)
 * @param ref - The reference key of the custom attribute
 * @returns The value of the custom attribute or undefined if not found
 */
export const getCustomAttribute = <
  T extends {
    customAttributes?: Array<CustomAttribute | null | undefined> | null;
  },
>(
  entity: T,
  ref: string,
): string | undefined => {
  return entity.customAttributes?.find((attr) => attr?.ref === ref)?.value;
};

/**
 * Helper to convert camelCase/snake_case to Title Case.
 *
 * @param text - The text to format.
 * @returns
 */
export const formatLabel = (text: string): string => {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const formatUserDisplayName = (u: User): string => {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  const primary = name || u.email;
  return `${primary}`;
};
